import React, { useMemo, useState } from "react";
import { useGSheet } from "@/hooks/useGSheet";
import { LiveHeader, StateBlock, Badge, crc, cell, fmtDate, phone } from "@/components/live/LiveShared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Loader2 } from "lucide-react";

const STANDARD_STAGES = [
  { key: "Preparacion", label: "Preparación" },
  { key: "Empaque y embalaje", label: "Empaque" },
  { key: "Despacho", label: "Despacho" },
];

const norm = (value) => String(value || "").trim().toLowerCase();

function actionGroupLabel(nextAction) {
  const action = norm(nextAction);
  if (!action) return "Revisar";
  if (action === "cobrar") return "Cobrar";
  if (action === "cobrar saldo") return "Cobrar saldo";
  if (action === "preparar") return "Preparar";
  if (action === "embalar") return "Empacar";
  if (action === "crear guía y despachar") return "Crear guía y despachar";
  if (action === "coordinar salida") return "Coordinar salida";
  if (action === "confirmar entrega") return "Confirmar entrega";
  if (action === "avisar al cliente") return "Avisar al cliente";
  return String(nextAction || "Revisar");
}

function completionAction(row) {
  const stage = norm(row.status);

  if (stage === "completado") return null;

  if (stage === "listo para retiro") {
    return {
      stage: "Completado",
      label: "Confirmar retiro",
      title: "Confirmar retiro",
      message: `¿Confirmar que la orden #${row.order_id} fue retirada por el cliente? Esto marcará la entrega como completada aunque todavía exista un saldo pendiente.`,
    };
  }

  if (stage === "salida domicilio / encomienda") {
    return {
      stage: "Completado",
      label: "Confirmar entrega",
      title: "Confirmar entrega",
      message: `¿Confirmar que la orden #${row.order_id} fue entregada? Esto marcará la entrega como completada aunque todavía exista un saldo pendiente.`,
    };
  }

  return {
    stage: "Completado",
    label: "Completar orden",
    title: "Completar orden",
    message: `¿Confirmar que el cliente ya recibió la orden #${row.order_id}? El estado de pago se mantiene independiente; si existe saldo pendiente, la orden seguirá visible en Cobrar.`,
  };
}

export default function LiveOperationsPage() {
  const { data, loading, error, refresh } = useGSheet("operations");
  const [movingId, setMovingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const executeMove = async (orderId, stage) => {
    setMovingId(`${orderId}-${stage}`);
    try {
      await api.post("/gsheet/move-stage", { order_id: String(orderId), stage });
      toast.success(`Orden #${orderId} → ${stage}`);
      await refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo actualizar la etapa.");
    } finally {
      setMovingId(null);
    }
  };

  const requestMove = (orderId, stage, title, message) => {
    setConfirmAction({ orderId, stage, title, message });
  };

  const confirmMove = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    await executeMove(action.orderId, action.stage);
  };

  const groups = useMemo(() => {
    const g = {};
    data.forEach((r) => {
      const key = actionGroupLabel(r.next_action);
      (g[key] = g[key] || []).push(r);
    });
    return g;
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <LiveHeader
        title="Operaciones (en vivo)"
        subtitle="OPERACIONES desde Google Sheets"
        count={data.length}
        loading={loading}
        onRefresh={refresh}
        testid="live-operations"
      />

      <StateBlock loading={loading} error={error} empty={!loading && !error && data.length === 0} onRetry={refresh}>
        <div className="space-y-6">
          {Object.entries(groups).map(([stage, rows]) => (
            <section key={stage} data-testid={`ops-group-${stage}`}>
              <h2 className="font-display font-bold text-foreground mb-3 flex items-center gap-2">
                <Truck className="h-4 w-4 text-accent" /> {stage}
                <span className="text-xs font-semibold text-muted-foreground">({rows.length})</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {rows.map((r, i) => {
                  const complete = completionAction(r);
                  const completing = complete && movingId === `${r.order_id}-${complete.stage}`;
                  const terminalStage = ["listo para retiro", "salida domicilio / encomienda", "completado"].includes(norm(r.status));

                  return (
                    <div key={`${r.order_id}-${i}`} className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={`ops-card-${r.order_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-mono font-bold text-foreground">#{cell(r.order_id)}</p>
                          <p className="font-medium text-foreground">{cell(r.customer)}</p>
                          <p className="text-xs text-muted-foreground">{phone(r.phone)} · {cell(r.channel)}</p>
                        </div>
                        <Badge value={r.payment_status} />
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground text-xs block">Entrega</span>{cell(r.delivery)}</div>
                        <div><span className="text-muted-foreground text-xs block">Fecha</span>{fmtDate(r.order_date)}</div>
                        <div><span className="text-muted-foreground text-xs block">Total</span><span className="font-mono">{crc(r.total)}</span></div>
                        <div><span className="text-muted-foreground text-xs block">Saldo</span><span className="font-mono">{crc(r.balance)}</span></div>
                      </div>

                      {r.next_action ? (
                        <p className="mt-3 text-xs font-semibold text-accent">Próxima acción: {cell(r.next_action)}</p>
                      ) : null}

                      <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
                        {!terminalStage && STANDARD_STAGES.map((s) => (
                          <Button
                            key={s.key}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={Boolean(movingId)}
                            onClick={() => executeMove(r.order_id, s.key)}
                            data-testid={`ops-move-${r.order_id}-${s.key.replace(/\s+/g, "-")}`}
                          >
                            {movingId === `${r.order_id}-${s.key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : s.label}
                          </Button>
                        ))}

                        {complete && (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={Boolean(movingId)}
                            onClick={() => requestMove(r.order_id, complete.stage, complete.title, complete.message)}
                            data-testid={`ops-complete-${r.order_id}`}
                          >
                            {completing ? <Loader2 className="h-3 w-3 animate-spin" /> : complete.label}
                          </Button>
                        )}

                        {norm(r.status) !== "completado" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={Boolean(movingId)}
                            onClick={() => requestMove(
                              r.order_id,
                              "Canceladas",
                              "Cancelar orden",
                              `¿Confirmar que el cliente canceló la orden #${r.order_id}? Esto marcará la orden como CANCELADA. Los pagos registrados no se revierten automáticamente.`
                            )}
                            data-testid={`ops-cancel-${r.order_id}`}
                          >
                            {movingId === `${r.order_id}-Canceladas` ? <Loader2 className="h-3 w-3 animate-spin" /> : "Cancelar orden"}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          La pantalla se agrupa por próxima acción. Pago y entrega son estados independientes: una orden entregada puede seguir en Cobrar, y una orden pagada puede seguir en Preparar.
        </p>
      </StateBlock>

      <Dialog open={Boolean(confirmAction)} onOpenChange={(open) => !open && !movingId && setConfirmAction(null)}>
        <DialogContent data-testid="operations-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmAction?.title || "Confirmar acción"}</DialogTitle>
            <DialogDescription>{confirmAction?.message || ""}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmAction(null)} disabled={Boolean(movingId)}>
              Volver
            </Button>
            <Button type="button" onClick={confirmMove} disabled={Boolean(movingId)} data-testid="operations-confirm-submit">
              {movingId ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
